import { zodResolver } from "@hookform/resolvers/zod";
import { useSetHandleMutation } from "Api/Mutations/Social/useSetHandleMutation";
import { HandleField } from "Components/Social/HandleField";
import { SocialTermsConsent } from "Components/Social/SocialTermsConsent/SocialTermsConsent";
import { FieldEditForm } from "Components/UserAccount/Shared/PublicInformation/FieldEditForm";
import { useUserInfoQuery } from "Auth/Api/Queries/useUserInfoQuery";
import { useHandleAvailabilityState } from "Hooks/Social/useHandleAvailabilityState";
import {
  getHandleFormSchema,
  type HandleFormModel,
} from "Schemas/handleSchema";
import { getUserInfoAsync } from "State/Auth/UserInfo/GetUserInfoState";
import { Resources, useResource } from "Translations/Resources";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { toast } from "sonner";

const HANDLE_RESOURCES = Resources.Social.Handle;

type Props = {
  onSuccess: () => void;
};

/**
 * Handle editor (handle card's mini dialog): live availability (debounced), saves via
 * PUT api/social/handle and refreshes user-info so the card picks up the new value.
 * Changing an existing handle releases the old one immediately.
 */
export const HandleForm = ({ onSuccess }: Props) => {
  const { t } = useResource();
  const dispatch = useDispatch();
  const { data: userInfo } = useUserInfoQuery();
  // First handle = consent to the social-features terms (ZS-1749); a change needs none.
  const isSocialTermsConsentRequired = !userInfo?.handle;

  const {
    handleSubmit,
    control,
    watch,
    formState: { isDirty, errors },
  } = useForm<HandleFormModel>({
    resolver: zodResolver(
      getHandleFormSchema(t, { isSocialTermsConsentRequired }),
    ),
    defaultValues: { handle: userInfo?.handle ?? "", acceptSocialTerms: false },
  });

  const availability = useHandleAvailabilityState(
    watch("handle"),
    userInfo?.handle,
  );
  const isSocialTermsConsentMissing =
    isSocialTermsConsentRequired && !watch("acceptSocialTerms");

  const { mutateAsync: setHandleAsync, isPending } = useSetHandleMutation({
    onError: (error) => {
      toast.error(
        t(
          error?.status === 409 && error.data?.errors?.Handle
            ? HANDLE_RESOURCES.Taken
            : HANDLE_RESOURCES.SaveError,
        ),
      );
    },
  });

  const onSubmit = async (data: HandleFormModel) => {
    try {
      await setHandleAsync(data);
    } catch {
      // Feedback shown by the mutation's onError — keep the dialog open.
      return;
    }

    dispatch(getUserInfoAsync.request(undefined));
    toast.success(t(Resources.Common.Saved));
    onSuccess();
  };

  return (
    <FieldEditForm
      onSubmit={handleSubmit(onSubmit)}
      isSubmitDisabled={
        !isDirty ||
        availability.isTaken ||
        availability.isUnchanged ||
        isSocialTermsConsentMissing
      }
      isSaving={isPending}
      submitLabel={
        isSocialTermsConsentRequired
          ? t(Resources.Social.Terms.AcceptAndContinue)
          : undefined
      }
    >
      <HandleField
        control={control}
        errors={errors}
        name="handle"
        availability={availability}
        autoFocus
        isChangeNoteVisible={!!userInfo?.handle}
      />
      {isSocialTermsConsentRequired && (
        <SocialTermsConsent
          control={control}
          errors={errors}
          name="acceptSocialTerms"
        />
      )}
    </FieldEditForm>
  );
};
